import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDemoModal } from "@/context/DemoModalContext";
import { getSubscribeUrl } from "@/config/api";
import { useToast } from "@/hooks/use-toast";
import { trackLinkedInConversion } from "@/components/LinkedInTracking";
import { getGa4ClientId } from "@/lib/analytics";
import { ArrowRight, X } from "lucide-react";

const CALENDLY_URL = "https://calendly.com/meshly/get-to-know-meshly";

export const DemoRequestModal = () => {
  const { isOpen, closeModal } = useDemoModal();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.company
    ) {
      toast({
        title: "Missing required fields",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const gaClientId = await getGa4ClientId();
      const response = await fetch(getSubscribeUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          list: "demo-request",
          metadata: {
            company: formData.company,
            source: "website-demo-modal",
            timestamp: new Date().toISOString(),
            ...(gaClientId !== undefined && { ga_client_id: gaClientId }),
          },
        }),
      });

      // Parse response
      let data: any = null;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await response.json();
        } catch (parseError) {
          console.warn("Failed to parse JSON response:", parseError);
        }
      }

      // If response is not OK, throw error
      if (!response.ok) {
        const message =
          data?.message || `Submission failed (${response.status})`;
        throw new Error(message);
      }

      // If we got here, the request was successful
      // Track LinkedIn conversion
      trackLinkedInConversion("demo_request_submitted");

      // Success - close modal first, then redirect to Calendly
      closeModal();

      // Use setTimeout to ensure modal closes before redirect
      // Use window.location.replace for reliable redirect (replaces history)
      setTimeout(() => {
        try {
          window.location.replace(CALENDLY_URL);
        } catch (redirectError) {
          // Fallback if replace fails
          console.warn("Redirect failed, trying href:", redirectError);
          window.location.href = CALENDLY_URL;
        }
      }, 150);
    } catch (error) {
      const origin =
        typeof window !== "undefined" ? window.location.origin : "";
      const isNetworkOrCors = error instanceof TypeError;
      const description = isNetworkOrCors
        ? `Your backend blocked requests from this domain (${origin}). This is usually a CORS allowlist issue.`
        : error instanceof Error
          ? error.message
          : "Please try again or contact us directly.";

      // eslint-disable-next-line no-console
      console.error("Demo request submission error:", error);

      toast({
        title: isNetworkOrCors ? "Request blocked (CORS)" : "Submission failed",
        description,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] p-0 gap-0 overflow-hidden border-0 bg-transparent">
        <div className="grid md:grid-cols-2 max-h-[90vh] overflow-y-auto">
          {/* Left Panel - Branding */}
          <div className="bg-gradient-to-br from-[#4B45E5] to-[#383CCC] p-6 md:p-12 flex flex-col justify-center relative">
            <button
              onClick={closeModal}
              className="absolute top-4 left-4 text-white/70 hover:text-white transition-colors md:hidden"
            >
              <X className="h-6 w-6" />
            </button>

            <div className="flex items-center gap-2 mb-4 md:mb-8 mt-6 md:mt-0">
              <img
                src="/logo-octopus.png"
                alt="Meshly"
                title="Meshly Logo"
                className="h-8 w-8 md:h-10 md:w-10 brightness-0 invert"
              />
              <span className="text-lg md:text-xl font-bold text-white">
                Meshly
              </span>
            </div>

            <h2 className="text-2xl md:text-4xl font-bold text-white mb-2 md:mb-4">
              Schedule a <span className="text-[#86efac]">Demo.</span>
            </h2>

            <p className="text-white/80 text-base md:text-lg">
              Connected is not controlled. See how Meshly catches revenue risk
              across your systems.
            </p>
          </div>

          {/* Right Panel - Form */}
          <div className="bg-white p-6 md:p-12 relative">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors hidden md:block"
            >
              <X className="h-6 w-6" />
            </button>

            <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                <div className="space-y-1 md:space-y-2">
                  <Label htmlFor="firstName" className="text-gray-700 text-sm">
                    First Name *
                  </Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    className="rounded-lg border-gray-200 focus:border-[#4B45E5] focus:ring-[#4B45E5] h-10"
                    required
                  />
                </div>
                <div className="space-y-1 md:space-y-2">
                  <Label htmlFor="lastName" className="text-gray-700 text-sm">
                    Last Name *
                  </Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    className="rounded-lg border-gray-200 focus:border-[#4B45E5] focus:ring-[#4B45E5] h-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1 md:space-y-2">
                <Label htmlFor="email" className="text-gray-700 text-sm">
                  Work Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="rounded-lg border-gray-200 focus:border-[#4B45E5] focus:ring-[#4B45E5] h-10"
                  required
                />
              </div>

              <div className="space-y-1 md:space-y-2">
                <Label htmlFor="company" className="text-gray-700 text-sm">
                  Company Name *
                </Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => handleInputChange("company", e.target.value)}
                  className="rounded-lg border-gray-200 focus:border-[#4B45E5] focus:ring-[#4B45E5] h-10"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#86efac] hover:bg-[#86efac]/90 text-gray-900 font-semibold rounded-full py-5 md:py-6 text-base md:text-lg mt-4 md:mt-6 flex items-center justify-center gap-2"
              >
                {isSubmitting ? "Submitting..." : "Schedule Demo"}
                {!isSubmitting && <ArrowRight className="h-5 w-5" />}
              </Button>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
